"use client";

import * as React from "react";
import { cva } from "class-variance-authority";
import { ChevronRight } from "lucide-react";

import { PortalScopeProvider, usePortalScope } from "../../lib/portal-scope";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "./sheet";
import { useSecondarySheetContext } from "./standard-sheet";

/** Props for the {@link SecondarySheet} component. */
export type SecondarySheetProps = {
	/** Whether the secondary sheet is currently visible. */
	isShown: boolean;
	/** Callback invoked when the sheet requests to close. */
	onClose: () => void;
	/** Heading text displayed in the neutral header bar. */
	title: string;
	/** Content rendered inside the scrollable body area. */
	children: React.ReactNode;
	/**
	 * Controls the maximum width of the sheet panel, using the same widths as
	 * {@link StandardSheet}. Use `"wide-screen"` for content that carries its own
	 * fixed width and would otherwise be clipped — a rendered email, for example,
	 * is a 560-600px document. Defaults to `"standard"`.
	 *
	 * `"wide-screen"` is responsive: it drops back to the standard width below the
	 * `xl` breakpoint. The panel is offset left by the primary sheet's width, so
	 * the pair needs roughly `primary + this` of viewport; below that the wider
	 * panel would be pushed off the left edge rather than merely looking cramped.
	 */
	variant?: "standard" | "wide-screen";
};

const secondarySheetContentVariants = cva(
	"w-full py-0 px-0 bg-neutral-50 flex flex-col h-screen !gap-y-0",
	{
		variants: {
			variant: {
				standard: "sm:max-w-lg",
				// Only widens at `xl`, which is >= 1280px under both the admin-app
				// breakpoint scale (1366px) and the Tailwind defaults Storybook uses
				// (1280px) — enough for a 512px primary sheet plus this 768px panel.
				"wide-screen": "sm:max-w-lg xl:max-w-3xl",
			},
		},
		defaultVariants: {
			variant: "standard",
		},
	},
);

export function SecondarySheet({
	isShown,
	onClose,
	title,
	children,
	variant = "standard",
}: SecondarySheetProps) {
	// State rather than a ref: the element is read while rendering, to scope the
	// portals below to this sheet. A ref would hand `PortalScopeProvider` `null`
	// on the render that mounts the sheet and never re-render to correct it.
	const [contentElement, setContentElement] =
		React.useState<HTMLDivElement | null>(null);
	const scrollableRef = React.useRef<HTMLDivElement>(null);
	const [primarySheetWidth, setPrimarySheetWidth] = React.useState(0);

	const portalContainer = usePortalScope();
	const secondarySheetContext = useSecondarySheetContext();

	React.useEffect(() => {
		secondarySheetContext?.setIsSecondaryOpen(isShown);

		return () => {
			secondarySheetContext?.setIsSecondaryOpen(false);
		};
	}, [isShown, secondarySheetContext]);

	React.useEffect(() => {
		if (isShown) {
			secondarySheetContext?.setOnSecondaryClose(() => onClose);
		} else {
			secondarySheetContext?.setOnSecondaryClose(null);
		}

		return () => {
			secondarySheetContext?.setOnSecondaryClose(null);
		};
	}, [isShown, onClose, secondarySheetContext]);

	// Measure the primary sheet width and keep it current. The primary node's width
	// can resolve after this sheet mounts (open transition / late layout), so observe
	// it rather than reading offsetWidth once — a single-shot read that lands on 0
	// would otherwise stick (setState(0) is a no-op) and leave the sheet stacked
	// directly behind the drawer (translateX(0)).
	React.useEffect(() => {
		if (!portalContainer) {
			return;
		}

		const measure = () => {
			const width = portalContainer.offsetWidth;
			setPrimarySheetWidth((prev) =>
				width > 0 && width !== prev ? width : prev,
			);
		};

		measure();

		const resizeObserver = new ResizeObserver(measure);
		resizeObserver.observe(portalContainer);

		return () => {
			resizeObserver.disconnect();
		};
	}, [portalContainer]);

	React.useEffect(() => {
		let cleanupFn: (() => void) | undefined;

		const timeoutId = setTimeout(() => {
			const scrollableElement = scrollableRef.current;
			if (!scrollableElement) {
				return;
			}

			const handleWheel = (e: WheelEvent) => {
				const { scrollHeight, clientHeight } = scrollableElement;
				const canScroll = scrollHeight > clientHeight;

				if (canScroll) {
					e.preventDefault();
					e.stopPropagation();
					scrollableElement.scrollTop += e.deltaY;
				}
			};

			scrollableElement.addEventListener("wheel", handleWheel, {
				passive: false,
			});

			cleanupFn = () => {
				scrollableElement.removeEventListener("wheel", handleWheel);
			};
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			cleanupFn?.();
		};
	}, [scrollableRef, isShown]);

	return (
		<Sheet
			open={isShown}
			onOpenChange={(open) => {
				if (!open) {
					onClose();
				}
			}}
			modal={false}
		>
			<SheetContent
				className={secondarySheetContentVariants({ variant })}
				ref={setContentElement}
				isOpen={isShown}
				displayCloseButton={false}
				hideOverlay
				style={{
					zIndex: primarySheetWidth ? 5001 : 40,
					transform: primarySheetWidth
						? `translateX(-${primarySheetWidth}px)`
						: "translateX(0)",
				}}
				onOpenAutoFocus={(e) => {
					e.preventDefault();
				}}
				onInteractOutside={(e) => {
					e.preventDefault();
				}}
			>
				<div className="px-6 bg-neutral-200 text-neutral-600 h-[60px] flex flex-none items-center justify-between">
					<SheetTitle className="text-base font-bold leading-normal text-neutral-600 dark:text-neutral-600">
						{title}
					</SheetTitle>
					<SheetClose>
						<ChevronRight
							className="h-4 w-4 text-neutral-600"
							strokeWidth={3}
						/>
						<span className="sr-only">Close</span>
					</SheetClose>
				</div>
				<PortalScopeProvider container={contentElement}>
					<div className="flex flex-col flex-1 min-h-0 overflow-hidden">
						<div
							className="p-6 flex-1 min-h-0 overflow-auto"
							ref={scrollableRef}
						>
							{children}
						</div>
					</div>
				</PortalScopeProvider>
			</SheetContent>
		</Sheet>
	);
}
